/**
 * Order totals for the embed checkouts, read off the server's charge preview.
 *
 * The embeds used to render `Tax $0.00` and no fee line at all, while the
 * charge itself went through the ordinary server path — which adds Stripe Tax
 * and, when the product has `passFeesToCustomer`, a platform fee. So the buyer
 * was quoted less than they were billed, and an Apple/Google Pay sheet built
 * from that quote authorized the wrong amount.
 *
 * POS and the customer app both solve this by never doing the arithmetic on the
 * client: they preview the charge (`charge-multiple-total[-guest]`, i.e. the
 * real charge path with `executeCharge: false`) and display what comes back.
 * These helpers do the same for the embeds, keeping POS's field precedence —
 * see `CartSummary.tsx`'s `liveTotals`.
 */
import type { ChargeProductResultDto } from "@zipper/embedded-api-client";
import { formatMinorAmount } from "../components/format-money";

/** Fee components, when the server broke the platform fee down for us. */
export interface FeeBreakdown {
  stripeProcessingFeeInCents: number;
  stripeInvoicingFeeInCents: number;
  zipperPlatformFeeInCents: number;
  paymentMethodType?: string | undefined;
}

/**
 * A quote in cents. `quoted: false` means nobody has priced this cart yet, so
 * `platformFee`/`tax` are unknown (NOT zero) and the UI must say so rather than
 * print a `$0.00` it cannot stand behind.
 */
export interface OrderTotals {
  quoted: boolean;
  subtotalInCents: number;
  discountInCents: number;
  platformFeeInCents: number;
  taxInCents: number;
  totalInCents: number;
  feeBreakdown?: FeeBreakdown | undefined;
  /** Amount billed each period after today, for subscription lines. */
  recurringTotalInCents: number;
}

function toCents(dollars: number): number {
  return Math.max(0, Math.round(dollars * 100));
}

/**
 * What to show before a preview exists: the cart's own arithmetic, with tax and
 * fees explicitly unknown.
 */
export function estimatedTotals(
  subtotalDollars: number,
  discountDollars = 0,
): OrderTotals {
  const subtotalInCents = toCents(subtotalDollars);
  const discountInCents = Math.min(subtotalInCents, toCents(discountDollars));
  return {
    quoted: false,
    subtotalInCents,
    discountInCents,
    platformFeeInCents: 0,
    taxInCents: 0,
    totalInCents: subtotalInCents - discountInCents,
    recurringTotalInCents: 0,
  };
}

function sum<T>(lines: T[], pick: (line: T) => number): number {
  return lines.reduce((total, line) => total + (pick(line) || 0), 0);
}

/**
 * Fold the preview's per-item results into one quote.
 *
 * `firstCharge` wins over the flat fields wherever it is present: for a
 * subscription (including a deferred intro) it is the authoritative quote for
 * what is billed today, and the flat fields describe the recurring price. This
 * is exactly POS's precedence — diverging from it would put a different number
 * in the embed than the staff member reads off the register.
 */
/**
 * The generated client does no runtime validation, so a field the API omits
 * arrives as `undefined` despite being typed `number` — and would sum to NaN,
 * printing "$NaN" where the price goes.
 */
function cents(value: number | null | undefined): number {
  return value ?? 0;
}

export function totalsFromPreview(
  lines: ChargeProductResultDto[],
): OrderTotals {
  // Nothing was priced, so nothing is quoted. Folding an empty response would
  // produce a confident `quoted: true` total of $0 and hide the real price
  // behind it.
  if (!lines.length) return estimatedTotals(0);
  return {
    quoted: true,
    subtotalInCents: sum(
      lines,
      (line) => line.firstCharge?.amountInCents ?? cents(line.subtotal),
    ),
    discountInCents: sum(
      lines,
      (line) =>
        line.firstCharge?.discountAmountInCents ?? cents(line.couponDiscount),
    ),
    platformFeeInCents: sum(
      lines,
      (line) => line.firstCharge?.feesInCents ?? line.platformFeeAmount ?? 0,
    ),
    taxInCents: sum(
      lines,
      (line) => line.firstCharge?.taxInCents ?? line.taxAmount ?? 0,
    ),
    totalInCents: sum(
      lines,
      (line) => line.firstCharge?.totalInCents ?? cents(line.total),
    ),
    feeBreakdown: mergeFeeBreakdowns(lines),
    recurringTotalInCents: sum(
      lines,
      (line) =>
        line.recurringCharge?.totalInCents ?? line.recurringFinalAmount ?? 0,
    ),
  };
}

function mergeFeeBreakdowns(
  lines: ChargeProductResultDto[],
): FeeBreakdown | undefined {
  const present = lines.filter((line) => line.platformFeeBreakdown);
  if (!present.length) return undefined;
  const paymentMethodType = present.find(
    (line) => line.platformFeeBreakdown?.paymentMethodType,
  )?.platformFeeBreakdown?.paymentMethodType;
  return {
    stripeProcessingFeeInCents: sum(
      present,
      (line) => line.platformFeeBreakdown?.stripeProcessingFeeInCents ?? 0,
    ),
    stripeInvoicingFeeInCents: sum(
      present,
      (line) => line.platformFeeBreakdown?.stripeInvoicingFeeInCents ?? 0,
    ),
    zipperPlatformFeeInCents: sum(
      present,
      (line) => line.platformFeeBreakdown?.zipperPlatformFeeInCents ?? 0,
    ),
    ...(paymentMethodType ? { paymentMethodType } : {}),
  };
}

/**
 * A preview line the server refused (`valid: false`) carries an error, not a
 * price. Summing those would quote a total for a cart that cannot be charged,
 * so callers fall back to the estimate instead.
 */
export function previewRejection(
  lines: ChargeProductResultDto[],
): string | undefined {
  const rejected = lines.find((line) => !line.valid);
  if (!rejected) return undefined;
  return rejected.errorMessage || "This purchase is no longer available.";
}

/** `£12.34` in the org currency (USD when unset), always two decimals. */
export function formatCents(cents: number, currency?: string | null): string {
  return formatMinorAmount(cents, currency);
}
